import { useMemo } from "react";
import { withMetaInfoFields } from "../../Helper/Analytics/tableMetaInfo";
import { usePagesContent } from "../../Hooks/usePagesContent";
import { FLEET_BREAKDOWN_DIMENSIONS } from "./fleetBreakdownDimensions";

/**
 * Entity attributes the Fleet Performance analytics API understands as metaInfo.
 *
 * The spellings are the API's, not ours: licencePlatNumber carries two
 * long-standing typos, and this module says originDepot where BMS Faults says
 * originDepo. Deriving against the wrong list would silently ask for a field the
 * backend ignores, so the list is per module rather than shared.
 */
export const FLEET_PERFORMANCE_META_INFO_FIELDS = [
  "licencePlatNumber",
  "licensePlateNumber",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
  "deviceID",
  "pingStatus",
  "fleetName",
  "fleetCategory",
];

/**
 * The FleetPerformance org meta keys under metaInfo.FleetPerformance.table.
 *
 * The four *VehiclesTable keys are the drilldown modals' and are named by each
 * chart's own config (FleetChartConfig.js orgMetaTableKeys). The three
 * *Table/-DetailsTable keys below are the tables that are not drilldowns: the
 * aggregated metrics list on the Energy Consumption tab and the vehicle list on
 * each chart-details page. They are collected here so one place shows a
 * customer every table they can configure.
 */
export const FLEET_PERFORMANCE_TABLE_KEYS = {
  ENERGY_CONSUMPTION_METRICS: "energyConsumptionMetricsTable",
  ENERGY_CONSUMPTION_TRENDS_DETAILS: "energyConsumptionTrendsDetailsTable",
  ENERGY_FLOW_TRENDS_DETAILS: "energyFlowTrendsDetailsTable",
};

/**
 * Just the columns an org configured for a table, or null.
 *
 * Kept separate from resolveFleetTableConfig because the metaInfo resolver must
 * derive only from what the ORG asked for. Deriving from the fallback as well
 * would widen every request on an unconfigured org - the fallback's own fields
 * are already in each request's base list by construction.
 */
export const getConfiguredFleetTableColumns = (orgMetaTables, tableKey) =>
  (tableKey ? (orgMetaTables || {})[tableKey] : null)?.columnsConfig || null;

/**
 * A table's columns, from FleetPerformance org meta where the org configures
 * them and from the chart's or module's own config otherwise.
 *
 * Only the columns are configurable. Everything else the caller's config carries
 * - the title, which may be a function of the clicked data point, and the
 * settings toggles - is passed through untouched, so configuring columns cannot
 * blank a heading or take a control away.
 */
/**
 * The order a table renders in, given what the org configured and the caller's own.
 *
 * getColumnsConfig builds columns by walking the ORDER, not the config map, so a
 * column an org adds to columnsConfig without naming in columnOrdering is never
 * built at all. Treating the two as either/or therefore silently swallowed a
 * configured column whenever the ordering was left out - and leaving it out is
 * the right thing to do for a table whose fallback order is computed per data
 * point, like the energy flow trends drilldown, which lists only the metric for
 * the series that was clicked.
 *
 * So the configured order wins when given, and otherwise the caller's order is
 * kept and anything the org configured beyond it is appended. Both halves stay
 * honoured: the per-series order survives and the extra column appears.
 */
const resolveColumnOrder = (columnsConfig, configuredOrder, fallbackOrder) => {
  if (configuredOrder) return configuredOrder;
  if (!columnsConfig) return fallbackOrder;

  const order = fallbackOrder || [];
  const extras = Object.keys(columnsConfig).filter((key) => !order.includes(key));
  return extras.length ? [...order, ...extras] : order;
};

export const resolveFleetTableConfig = (orgMetaTables, tableKey, fallback) => {
  const columnsConfig = getConfiguredFleetTableColumns(orgMetaTables, tableKey);
  if (!columnsConfig) return fallback;

  return {
    ...fallback,
    tableColumnsConfig: columnsConfig,
    tableColumnOrder: resolveColumnOrder(
      columnsConfig,
      (tableKey ? (orgMetaTables || {})[tableKey] : null)?.columnOrdering,
      fallback?.tableColumnOrder
    ),
  };
};

/**
 * The metaInfo a request should ask for: whatever the chart already asked for,
 * plus the entity attributes the columns on show need.
 *
 * Each call site keeps its own base list. They are not identical - the CSV and
 * mail mirrors drop deviceID and pingStatus, which only the on-screen renderers
 * read - and flattening them to one list would quietly widen those requests.
 */
export const buildFleetMetaInfoResolver = (columnsConfig) => (baseFields) =>
  withMetaInfoFields(baseFields, columnsConfig, FLEET_PERFORMANCE_META_INFO_FIELDS);

/**
 * Fields a column can name that are never an analytics `select` metric.
 *
 * Dimensions are not listed here: they are derived from the two lists that
 * already decide how a dimension is requested - FLEET_PERFORMANCE_META_INFO_FIELDS
 * (asked for through metaInfo) and FLEET_BREAKDOWN_DIMENSIONS (asked for through
 * groupBy) - plus whatever the request itself groups by or asks metaInfo for.
 *
 * What is left are values built from other fields: the row id, and the kWh/km
 * bounds string, which the server assembles from MinkWhPerKm/MaxkWhPerKm and the
 * Energy Consumption details table assembles client-side under `bounds`.
 */
export const FLEET_NON_METRIC_COLUMN_FIELDS = [
  "xLabel",
  "id",
  "bounds",
  "Bounds of Energy Consumed per Km (kWh/km)",
];

// Columns that render a control rather than a value: nothing to request or export.
const NON_DATA_RENDERING_TYPES = ["details-link", "action-menu"];

const DIMENSION_FIELDS = [
  ...FLEET_PERFORMANCE_META_INFO_FIELDS,
  ...FLEET_BREAKDOWN_DIMENSIONS.map((dimension) => dimension.value),
];

/** [mapKey, column] pairs for a columnsConfig map or an array of columns. */
const columnEntries = (columnsConfig) => {
  if (Array.isArray(columnsConfig)) {
    return columnsConfig.map((column) => [undefined, column]);
  }
  return Object.entries(columnsConfig || {});
};

const isDataColumn = (column) =>
  !!column && !NON_DATA_RENDERING_TYPES.includes(column.valueRenderingType);

/**
 * The metric names a table's columns read or sort by, in column order.
 *
 * Every column the config defines is included, shown or not, so toggling a
 * column in the picker never needs a refetch. A column is named by its
 * dataIndex, then its key, then its map key - the same fallback getColumnsConfig
 * uses - and its sortKey is added because the server can only sort by a metric
 * it selected.
 *
 * `requestShape.groupBy` and `requestShape.metaInfo` are the request's own lists:
 * a field the request already gets that way is not asked for again as a metric.
 * Asking for it would not fail - the API ignores a name it cannot aggregate -
 * but it answers with a null metric of that name, which the row builders spread
 * over the real value.
 */
export const deriveFleetSelectFields = (columnsConfig, requestShape = {}) => {
  const excluded = new Set([
    ...DIMENSION_FIELDS,
    ...FLEET_NON_METRIC_COLUMN_FIELDS,
    ...(requestShape.groupBy || []),
    ...(requestShape.metaInfo || []),
  ]);

  const fields = [];
  const add = (field) => {
    if (typeof field !== "string" || !field || excluded.has(field)) return;
    if (!fields.includes(field)) fields.push(field);
  };

  columnEntries(columnsConfig).forEach(([mapKey, column]) => {
    if (!isDataColumn(column)) return;
    add(column.dataIndex || column.key || mapKey);
    add(column.sortKey);
  });
  return fields;
};

/**
 * A resolver that widens a request's own `select` with what the table's columns
 * need.
 *
 * The base list stays first and unchanged - it holds what the request needs for
 * its own sake (a default sort, the inputs to a derived bounds column, a range
 * filter's metric) - and the columns' metrics are appended after it. A union,
 * never a replacement, so an org's config can only ever add to a request.
 */
export const buildFleetSelectResolver = (columnsConfig) => (baseSelect, requestShape = {}) => {
  const merged = [...(baseSelect || [])];
  deriveFleetSelectFields(columnsConfig, requestShape).forEach((field) => {
    if (!merged.includes(field)) merged.push(field);
  });
  return merged;
};

/** A column's header text for an export, which must equal its on-screen header. */
const exportHeaderOf = (column, field) => {
  if (typeof column?.title === "string" && column.title) return column.title;
  if (typeof column?.headerLabel === "string" && column.headerLabel) return column.headerLabel;
  return field;
};

/**
 * The `columns` and `columnOrder` an analytics download/mail request sends, from
 * a resolved table config.
 *
 * The header is the column's title because that is the on-screen header, and the
 * table's CSV step keeps only the headers it is showing (GenericDraggableTable
 * onDownloadCSV) - a header that differs from the title is silently dropped.
 *
 * `exportDataIndex` lets a column read a different field in the export than on
 * screen, for a value the screen builds itself: the Energy Consumption details
 * table renders `bounds` client-side, where the export's flat row carries it
 * under the server's own name.
 *
 * `leadingColumns` are identity columns a download carries ahead of the table's
 * own. Never the internal batteryID: it must not reach a downloaded or emailed
 * file (the Aggregated Metrics export leads with the org-facing device id).
 */
export const buildFleetExportColumns = (columnsConfig, columnOrder, leadingColumns = []) => {
  const config = columnsConfig || {};
  const order = Array.isArray(columnOrder) && columnOrder.length
    ? columnOrder
    : columnEntries(config).map(([mapKey, column]) => mapKey || column?.key || column?.dataIndex);

  const columns = [...leadingColumns];
  order.forEach((columnKey) => {
    const column = Array.isArray(config)
      ? config.find((c) => (c?.key || c?.dataIndex) === columnKey)
      : config[columnKey];
    if (!isDataColumn(column)) return;
    const field = column.exportDataIndex || column.dataIndex || column.key || columnKey;
    if (!field) return;
    const headerLabel = exportHeaderOf(column, field);
    if (columns.some((c) => c.headerLabel === headerLabel)) return;
    columns.push({ dataIndex: field, headerLabel });
  });

  return { columns, columnOrder: columns.map((column) => column.headerLabel) };
};

/**
 * A Fleet Performance table's columns, its metaInfo and select resolvers and its
 * export columns, from org meta where the org configures them and from the
 * module's own constants otherwise.
 *
 * The same wiring the drilldown hook does (Components/FleetChartDrillDown/
 * useFleetChartDrillDown.js), for the tables that are not drilldowns: the two
 * chart-details vehicle lists and the aggregated metrics list.
 *
 * Pass module-level constants as the fallbacks. The result is memoized on their
 * identity, so a literal built during render would hand every render a new
 * config object and reset the caller's selected columns.
 */
export const useFleetTableConfig = (
  tableKey,
  fallbackColumnsConfig,
  fallbackColumnOrder
) => {
  const pagesContent = usePagesContent();
  const orgMetaTables = pagesContent?.metaInfo?.FleetPerformance?.table;

  // Keyed on what this ONE table configured, not on the whole FleetPerformance
  // table block. The block goes undefined -> object when the page meta lands, so
  // depending on it hands back a new resolveMetaInfo to every org that
  // configures ANY Fleet Performance table - including every org already
  // configuring the four drilldowns - and refires the callers' fetch effect for
  // a value that did not change. Both of these stay undefined for an org that
  // configures none of these three keys, so the identity holds across that
  // transition and the request fires once.
  const configuredColumns = getConfiguredFleetTableColumns(orgMetaTables, tableKey);
  const configuredOrdering = (tableKey ? (orgMetaTables || {})[tableKey] : null)
    ?.columnOrdering;

  return useMemo(() => {
    const tableColumnsConfig = configuredColumns || fallbackColumnsConfig;
    const tableColumnOrder = resolveColumnOrder(
      configuredColumns,
      configuredOrdering,
      fallbackColumnOrder
    );
    return {
      tableColumnsConfig,
      tableColumnOrder,
      // Wraps a request's own base list. Derived from the org's columns alone,
      // so an unconfigured table keeps its request as it was.
      resolveMetaInfo: buildFleetMetaInfoResolver(configuredColumns),
      // Derived from the RESOLVED columns, fallback included: the select list is
      // what makes a column's value arrive at all, so a fallback column renamed
      // to a new metric has to be requested too. Same memo as the rest, so it
      // keeps its identity exactly as long as resolveMetaInfo does.
      resolveSelect: buildFleetSelectResolver(tableColumnsConfig),
      // Every resolved column, in table order, for the download/mail request.
      exportColumns: buildFleetExportColumns(tableColumnsConfig, tableColumnOrder),
    };
  }, [
    configuredColumns,
    configuredOrdering,
    fallbackColumnsConfig,
    fallbackColumnOrder,
  ]);
};
